// ===========================================
// Bookable terms for admin pre-booking
// ===========================================
// Pre-booking is *specifically* for setting up a term ahead of time, so a
// not-yet-started term (e.g. during school holidays) must still be selectable.
// These helpers decide which terms an admin can pre-book and which to default to.
//
// Two different questions live here:
//  - `getBookableTerms`: FUTURE date tiles (running + upcoming). Pure meaning - "has the term
//    finished" - answered on the SCHOOL's day (D11), never the UTC instant.
//  - `getPreBookableTerms` (`utils/termLock`): which terms a clinic may be pre-booked into - active
//    and not LOCKED, so a finished term stays offered until the next one starts.

import { schoolTodayUtcMidnight } from './dateUtils';

export interface TermLike {
  id: string;
  startDate: string;
  endDate: string;
  isActive: boolean;
}

/**
 * The calendar day "today" is, as a UTC midnight, for comparing against `@db.Date` term bounds.
 * With a school `timeZone` it is the SCHOOL's day (`schoolTodayUtcMidnight`); without one it falls
 * back to the UTC day. Comparing a term's UTC-midnight `endDate` to a raw `now` instant dropped a
 * term ending today from 00:00 UTC (11:00 Sydney) on its LAST day.
 */
function termDay(now: Date, timeZone?: string): number {
  return timeZone
    ? schoolTodayUtcMidnight(timeZone, now).getTime()
    : Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
}

/**
 * Terms an admin can pre-book: the currently-running one (if any) plus all
 * upcoming terms - never ended terms. Sorted soonest-first.
 */
export function getBookableTerms<T extends TermLike>(
  terms: T[] | undefined,
  now: Date = new Date(),
  timeZone?: string
): T[] {
  const today = termDay(now, timeZone);
  return (terms ?? [])
    .filter((t) => t.isActive && new Date(t.endDate).getTime() >= today)
    .sort((a, b) => new Date(a.startDate).getTime() - new Date(b.startDate).getTime());
}

/** The term running right now within the bookable set, or undefined (e.g. holidays). */
export function getRunningTerm<T extends TermLike>(
  bookableTerms: T[],
  now: Date = new Date(),
  timeZone?: string
): T | undefined {
  const today = termDay(now, timeZone);
  return bookableTerms.find(
    (t) => new Date(t.startDate).getTime() <= today && new Date(t.endDate).getTime() >= today
  );
}

/**
 * Default term id for the selector (plan FE8): the running term if one exists, else the nearest
 * UPCOMING term (startDate on or after the school's today), else the latest remaining term (a
 * finished term that is still unlocked), else null. `bookableTerms` is sorted soonest-first.
 * An ended-but-open term must never beat an upcoming one in the holidays.
 */
export function getDefaultTermId<T extends TermLike>(
  bookableTerms: T[],
  now: Date = new Date(),
  timeZone?: string
): string | null {
  const running = getRunningTerm(bookableTerms, now, timeZone);
  if (running) return running.id;
  const today = termDay(now, timeZone);
  const upcoming = bookableTerms
    .filter((t) => new Date(t.startDate).getTime() >= today)
    .sort((a, b) => new Date(a.startDate).getTime() - new Date(b.startDate).getTime())[0];
  if (upcoming) return upcoming.id;
  const latest = [...bookableTerms].sort(
    (a, b) => new Date(b.startDate).getTime() - new Date(a.startDate).getTime()
  )[0];
  return latest?.id ?? null;
}
